import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  LuCheckCheck,
  LuMousePointerClick,
  LuLock,
  LuLayoutList,
  LuCircleAlert,
} from 'react-icons/lu';
import {
  PhonicsLesson,
  PhonicsClassStatus,
  PhonicsClassLessonStatus,
  PhonicsLessonProgress,
} from '@/api/types';
import { useTooltip, TooltipPosition } from '@/context/Ui';
import { truncate } from '@/utils/strings';
import { GreenBox } from '@/components';
import { ManageLaunchLessonLink } from './ManageLaunchLessonLink';

type PodIconColor = 'red' | 'green' | 'black';

interface PodIconProps {
  className?: string;
  size?: 'md' | 'lg';
  tooltipText?: string;
  tooltipPosition?: TooltipPosition;
  ariaLabel?: string;
  color?: PodIconColor;
  inverted?: boolean;
  onClick?: () => void;
}

const PodIcon = ({
  children,
  className,
  size = 'md',
  tooltipText,
  tooltipPosition = 'left',
  ariaLabel,
  color = 'green',
  inverted = false,
  onClick,
}: React.PropsWithChildren<PodIconProps>) => {
  const { tooltipElementRef } = useTooltip(tooltipText, {
    position: tooltipPosition,
  });

  const fullClassName = clsx(
    'p-1 rounded-full',
    size === 'md' && 'w-8 h-8',
    size === 'lg' && 'w-12 h-12',
    color === 'red' &&
      (inverted ? 'text-red-500 bg-white' : 'bg-red-500 text-white'),
    color === 'green' &&
      (inverted ? 'text-green-dark bg-white' : 'bg-green-dark text-white'),
    color === 'black' &&
      (inverted ? 'text-black bg-white' : 'bg-black text-white'),
    className,
  );

  return onClick ? (
    <button
      ref={tooltipElementRef as React.RefObject<HTMLButtonElement | null>}
      className={clsx(
        color === 'red' &&
          (inverted ? 'hover:text-red-700' : 'hover:bg-red-700'),
        color === 'green' &&
          (inverted ? 'hover:text-green-darker' : 'hover:bg-green-darker'),
        color === 'black' &&
          (inverted ? 'hover:text-neutral-800' : 'bg-neutral-800'),
        fullClassName,
      )}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  ) : (
    <div
      ref={tooltipElementRef as React.RefObject<HTMLDivElement | null>}
      className={fullClassName}
    >
      {children}
    </div>
  );
};

interface PodIconCompletedProps {
  className?: string;
  size?: 'md' | 'lg';
  tooltipPosition?: TooltipPosition;
  color?: PodIconColor;
}

const PodIconCompleted = ({
  className,
  size = 'md',
  tooltipPosition = 'left',
  color = 'green',
}: PodIconCompletedProps) => (
  <PodIcon
    className={className}
    size={size}
    tooltipText="Your class has completed this lesson"
    tooltipPosition={tooltipPosition}
    ariaLabel="Your class has completed this lesson"
    color={color}
  >
    <LuCheckCheck
      className={clsx(
        size === 'md' && 'w-6 h-6 p-1',
        size === 'lg' && 'w-10 h-10 p-2',
      )}
    />
  </PodIcon>
);

interface PodIconNoteAlertProps {
  className?: string;
  size?: 'md' | 'lg';
  tooltipPosition?: TooltipPosition;
  color?: PodIconColor;
  onClick?: () => void;
}

const PodIconNoteAlert = ({
  className,
  size = 'md',
  tooltipPosition = 'left',
  color = 'red',
  onClick,
}: PodIconNoteAlertProps) => (
  <PodIcon
    className={className}
    size={size}
    tooltipText="Notes have not been added for this lesson"
    tooltipPosition={tooltipPosition}
    ariaLabel="Notes have not been added for this lesson"
    color={color}
    onClick={onClick}
  >
    <LuCircleAlert
      className={clsx(
        size === 'md' && 'w-6 h-6 p-1',
        size === 'lg' && 'w-10 h-10 p-2',
      )}
    />
  </PodIcon>
);

interface PodIconLaunchProps {
  className?: string;
  size?: 'md' | 'lg';
  tooltipPosition?: TooltipPosition;
  color?: PodIconColor;
  onClick?: () => void;
}

const PodIconLaunch = ({
  className,
  size = 'md',
  tooltipPosition = 'left',
  color = 'green',
  onClick,
}: PodIconLaunchProps) => (
  <PodIcon
    className={className}
    size={size}
    tooltipText="Launch the lesson in reading mode"
    tooltipPosition={tooltipPosition}
    ariaLabel="Launch the lesson in reading mode"
    color={color}
    onClick={onClick}
    inverted
  >
    <LuMousePointerClick
      className={clsx(
        'animate-ping',
        size === 'md' && 'w-6 h-6 p-1',
        size === 'lg' && 'w-10 h-10 p-2',
      )}
    />
  </PodIcon>
);

interface PodIconLockedProps {
  className?: string;
  size?: 'md' | 'lg';
  tooltipPosition?: TooltipPosition;
  color?: PodIconColor;
}

const PodIconLocked = ({
  className,
  size = 'md',
  tooltipPosition = 'left',
  color = 'green',
}: PodIconLockedProps) => (
  <PodIcon
    className={className}
    size={size}
    tooltipText="Your class is currently not able to advance to this lesson"
    tooltipPosition={tooltipPosition}
    ariaLabel="Your class is currently not able to advance to this lesson"
    color={color}
  >
    <LuLock
      className={clsx(
        size === 'md' && 'w-6 h-6 p-1',
        size === 'lg' && 'w-10 h-10 p-2',
      )}
    />
  </PodIcon>
);

interface PodIconViewResultsProps {
  className?: string;
  size?: 'md' | 'lg';
  tooltipPosition?: TooltipPosition;
  color?: PodIconColor;
  onClick?: () => void;
}

const PodIconViewResults = ({
  className,
  size = 'md',
  tooltipPosition = 'left',
  color = 'green',
  onClick,
}: PodIconViewResultsProps) => (
  <PodIcon
    className={className}
    size={size}
    tooltipText="View results for this lesson"
    tooltipPosition={tooltipPosition}
    ariaLabel="View results for this lesson"
    color={color}
    onClick={onClick}
  >
    <LuLayoutList
      className={clsx(
        size === 'md' && 'w-6 h-6 p-1',
        size === 'lg' && 'w-10 h-10 p-2',
      )}
    />
  </PodIcon>
);

interface PodContentProps {
  onClick?: () => void;
  centred?: boolean;
  ariaLabel?: string;
}

const PodContent = ({
  children,
  onClick,
  centred = true,
  ariaLabel,
}: React.PropsWithChildren<PodContentProps>) =>
  onClick ? (
    <button
      className={clsx(
        'absolute inset-0 flex',
        centred ? 'items-center justify-center' : 'p-3',
      )}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  ) : (
    <div
      className={clsx(
        'absolute inset-0 flex',
        centred ? 'items-center justify-center' : 'p-3',
      )}
      aria-label={ariaLabel}
    >
      {children}
    </div>
  );

interface ManageLessonPodProps {
  size?: 'md' | 'lg';
  inverted?: boolean;
  lesson: PhonicsLesson;
  phonicsClass: PhonicsClassStatus;
  currentLessonId?: string;
  onViewDetail?: () => void;
  onViewResults?: () => void;
  onLaunchLesson?: () => void;
  onAddNotes?: () => void;
}

export const ManageLessonPod = ({
  size = 'md',
  inverted = false,
  lesson,
  phonicsClass,
  currentLessonId,
  onViewDetail,
  onViewResults,
  onLaunchLesson,
  onAddNotes,
}: ManageLessonPodProps) => {
  const [isCurrentLesson, setIsCurrentLesson] = useState(false);

  const [lessonClassStatus, setLessonClassStatus] = useState<
    PhonicsClassLessonStatus | undefined
  >();

  const isCompleted =
    lessonClassStatus?.progress === PhonicsLessonProgress.COMPLETED;
  const isNotStarted =
    lessonClassStatus?.progress === PhonicsLessonProgress.NOT_STARTED ||
    !lessonClassStatus?.progress;

  const hasLessonNotes = !!lessonClassStatus?.lessonNotes;

  const getBgShade = () => {
    if (inverted) {
      return 'wt';
    }

    if (
      lessonClassStatus?.progress === PhonicsLessonProgress.IN_PROGRESS ||
      lesson.id === currentLessonId
    ) {
      return 'dk';
    }

    if (lessonClassStatus?.progress === PhonicsLessonProgress.COMPLETED) {
      return 'md';
    }

    return 'lt';
  };

  const renderPodContent = () => (
    <PodContent
      onClick={onViewDetail}
      centred={!lesson.isReview}
      ariaLabel="View lesson details"
    >
      {isCompleted && (
        <PodIconCompleted
          className="absolute top-1 left-1"
          tooltipPosition="right"
        />
      )}

      {isNotStarted && !isCurrentLesson && (
        <PodIconLocked className="absolute bottom-1 right-1" />
      )}

      {lesson.isReview ? (
        <div
          className="h-full flex flex-col text-left"
          aria-label={`${lesson.number}`}
        >
          <div className="h-1/3">Review</div>

          <div className="h-1/3 font-sassoonInfant text-lg text-wrap">
            {lesson?.sounds && lesson?.sounds.length ? (
              <h3 title={lesson?.sounds?.map((sound) => sound.name).join('')}>
                {truncate(
                  lesson?.sounds?.map((sound) => sound.name).join('') || '',
                )}
              </h3>
            ) : (
              <h3
                title={lesson?.patterns
                  ?.map((pattern) => pattern.name)
                  .join(' ')}
              >
                {truncate(
                  lesson?.patterns?.map((pattern) => pattern.name).join(' ') ||
                    '',
                )}
              </h3>
            )}
          </div>
        </div>
      ) : lesson?.sounds && lesson?.sounds.length ? (
        <div className="font-sassoonInfant">{lesson.sounds?.[0]?.name}</div>
      ) : (
        <div className="font-sassoonInfant text-lg">
          {lesson.patterns?.[0]?.name}
        </div>
      )}
    </PodContent>
  );

  useEffect(() => {
    if (!lessonClassStatus) {
      return;
    }

    setIsCurrentLesson(
      lesson.id === currentLessonId ||
        lessonClassStatus?.progress === PhonicsLessonProgress.IN_PROGRESS,
    );
  }, [lesson, currentLessonId, lessonClassStatus]);

  useEffect(() => {
    setLessonClassStatus(
      phonicsClass.classLessonStatuses.find(
        (status) => status.lessonId === lesson.id,
      ),
    );
  }, [lesson, phonicsClass]);

  return (
    <GreenBox
      key={lesson.id}
      className={clsx(lesson.isExtra && 'text-gray-400')}
      bg={getBgShade()}
      size={size}
      smallText={lesson.isReview}
      highlighted={isCurrentLesson}
    >
      {renderPodContent()}

      {isCurrentLesson && onLaunchLesson && (
        <ManageLaunchLessonLink
          lesson={lesson}
          phonicsClass={phonicsClass}
          naked
          onLaunchLesson={onLaunchLesson}
        >
          <PodIconLaunch className="absolute top-1 right-1" />
        </ManageLaunchLessonLink>
      )}

      {isCompleted && !hasLessonNotes && onAddNotes && (
        <PodIconNoteAlert
          className="absolute top-1 right-1 hidden"
          onClick={onAddNotes}
        />
      )}

      {!isNotStarted && onViewResults && (
        <PodIconViewResults
          className="absolute bottom-1 left-1"
          onClick={onViewResults}
          tooltipPosition="right"
        />
      )}
    </GreenBox>
  );
};
